import * as signalR from '@microsoft/signalr';

class RealtimeVehicleService {
  private static instance: RealtimeVehicleService | null = null;
  private connection: signalR.HubConnection | null = null;
  private isStarting = false;
  private subscribedRoutes = new Set<number>();
  private dataListeners = new Set<(data: any) => void>();
  private statusListeners = new Set<(connected: boolean) => void>();

  // Progressive backoff timings (ms): starts at 2s (avoiding 0ms tight loops)
  private retryDelays = [2000, 5000, 10000, 30000];
  private retryCount = 0;
  private reconnectTimeoutId: any = null;

  private constructor() {
    this.initConnection();
    this.setupPageLifecycle();
  }

  public static getInstance(): RealtimeVehicleService {
    if (!RealtimeVehicleService.instance) {
      RealtimeVehicleService.instance = new RealtimeVehicleService();
    }
    return RealtimeVehicleService.instance;
  }

  private initConnection() {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_SERVER_URL}/SignalRHubs/RealtimeVehicle`, {
        accessTokenFactory: () => '',
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // If offline, do not attempt reconnect; online event will trigger reconnect
          if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return null;
          }

          // If rate limited, enforce 30s backoff
          if (
            retryContext.retryReason?.message?.includes('429') ||
            `${retryContext.retryReason}`.includes('429')
          ) {
            return 30000;
          }

          // Stop internal retries after 4 attempts and let manual backoff handle it cleanly
          if (retryContext.previousRetryCount >= 4) {
            return null;
          }

          // Progressive backoff starting at 2s with jitter
          const delay =
            this.retryDelays[
              Math.min(retryContext.previousRetryCount, this.retryDelays.length - 1)
            ];
          const jitter = Math.floor(Math.random() * 1000);
          return delay + jitter;
        },
      })
      .configureLogging(signalR.LogLevel.None)
      .build();

    // Single BroadcastEta listener distributing to all active component listeners
    this.connection.on('BroadcastEta', (data: any) => {
      this.dataListeners.forEach((listener) => {
        try {
          listener(data);
        } catch (err) {
          // Silent handling to avoid Sentry billing spam
        }
      });
    });

    this.connection.onreconnecting(() => {
      this.notifyStatus(false);
    });

    this.connection.onreconnected(async () => {
      this.retryCount = 0;
      this.notifyStatus(true);
      await this.resubscribeAll();
    });

    this.connection.onclose((error) => {
      this.notifyStatus(false);
      this.scheduleManualReconnect(error);
    });
  }

  private notifyStatus(connected: boolean) {
    this.statusListeners.forEach((listener) => {
      try {
        listener(connected);
      } catch (err) {
        // Silent handling
      }
    });
  }

  public subscribeData(listener: (data: any) => void): () => void {
    this.dataListeners.add(listener);
    return () => {
      this.dataListeners.delete(listener);
    };
  }

  public subscribeStatus(listener: (connected: boolean) => void): () => void {
    this.statusListeners.add(listener);
    const isConnected =
      this.connection?.state === signalR.HubConnectionState.Connected;
    listener(isConnected);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  public async start(): Promise<void> {
    if (!this.connection) return;

    // Strict state guard: do NOT start if already Connected, Connecting, or Reconnecting
    if (
      this.connection.state !== signalR.HubConnectionState.Disconnected ||
      this.isStarting
    ) {
      return;
    }

    // Do not attempt if offline or tab hidden
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }

    this.isStarting = true;
    try {
      await this.connection.start();
      this.retryCount = 0;
      this.isStarting = false;
      this.notifyStatus(true);
      await this.resubscribeAll();
    } catch (err: any) {
      this.isStarting = false;
      this.notifyStatus(false);
      this.scheduleManualReconnect(err);
    }
  }

  public async stop(): Promise<void> {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    if (
      this.connection &&
      this.connection.state !== signalR.HubConnectionState.Disconnected
    ) {
      try {
        await this.connection.stop();
      } catch (err) {
        // Ignored on clean stop
      }
    }
    this.notifyStatus(false);
  }

  private scheduleManualReconnect(error?: any) {
    if (this.reconnectTimeoutId) return;

    // Do not schedule if offline or page is hidden; native events will resume when ready
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    if (typeof document !== 'undefined' && document.hidden) return;

    let delay =
      this.retryDelays[Math.min(this.retryCount, this.retryDelays.length - 1)];
    if (error?.message?.includes('429') || `${error}`.includes('429')) {
      delay = 30000;
    }

    const jitter = Math.floor(Math.random() * 1000);
    const finalDelay = delay + jitter;

    this.retryCount++;
    this.reconnectTimeoutId = setTimeout(async () => {
      this.reconnectTimeoutId = null;
      await this.start();
    }, finalDelay);
  }

  public async setSubscribedRoutes(routeIds: number[]): Promise<void> {
    this.subscribedRoutes = new Set(routeIds);
    await this.resubscribeAll();
  }

  private async resubscribeAll(): Promise<void> {
    if (this.connection?.state !== signalR.HubConnectionState.Connected) return;

    for (const routeId of Array.from(this.subscribedRoutes)) {
      try {
        await this.connection.invoke('JoinGroup', `route:${routeId}`);
      } catch (err) {
        // Silent handling
      }
    }
  }

  private setupPageLifecycle() {
    if (typeof window === 'undefined') return;

    // Native online event: resumes connection when internet access returns
    window.addEventListener('online', async () => {
      this.retryCount = 0;
      await this.start();
    });

    // Native offline event: pauses retries to avoid connection storm
    window.addEventListener('offline', () => {
      if (this.reconnectTimeoutId) {
        clearTimeout(this.reconnectTimeoutId);
        this.reconnectTimeoutId = null;
      }
      this.notifyStatus(false);
    });

    // Page visibility event: pause in background, resume in foreground
    document.addEventListener('visibilitychange', async () => {
      if (document.hidden) {
        if (this.reconnectTimeoutId) {
          clearTimeout(this.reconnectTimeoutId);
          this.reconnectTimeoutId = null;
        }
      } else {
        if (this.connection?.state === signalR.HubConnectionState.Disconnected) {
          await this.start();
        }
      }
    });
  }
}

export default RealtimeVehicleService.getInstance();
